import React, { useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { Checkbox, Heading } from "@walmart-web/livingdesign-components"
import Select from "components/Shared/Filters/Select"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import { Rol, UserRequired } from "components/UserManagement/interfaces"
import { getOrganizations, getRoles } from "components/UserManagement/service"
import Input from "./Input"
import Skeleton from "./Skeleton"
import { mapData, UserFields } from "./utils"
import { isEmailValidator, isEmptyString } from "../Validators/validators"
import "./index.scss"
import CheckboxValidatorTitle from "../Validators/CheckboxValidatorTitle"

interface FormUsersProps {
	data?: UserRequired
	setData?: (data: UserRequired) => void
	initialValue?: boolean
	disabled?: Record<UserFields, boolean>
}

const initialFormData: UserRequired = {
	firstName: "",
	lastName: "",
	organization: { id: "", name: "" },
	email: "",
	roles: [],
}

const isFetchingComponent = (array: Array<boolean>) =>
	array.reduce((prev, curr) => prev || curr)

const onChangeCheckbox = (roles: string[], rol: string) => {
	if (roles.includes(rol)) {
		roles.splice(roles.indexOf(rol), 1)
		return [...roles]
	}
	return [...roles, rol]
}

const FormUsers: React.FC<FormUsersProps> = ({
	data = initialFormData,
	setData,
	disabled,
	initialValue = false,
}) => {
	const [formData, setFormData] = useState<UserRequired>(data)

	const { data: rolesData = [], isFetching: isFetchingRoles } = useFetchData(
		"retrieveRoles",
		getRoles
	)

	const {
		data: organizationsData = [],
		isFetching: isFetchingOrganizations,
	} = useFetchData("retrieveOrganizations", getOrganizations)

	useEffect(() => {
		if (setData) setData(formData)
	}, [formData])

	const organizationMappedData = mapData(
		organizationsData.organizations ?? [],
		{ name: "name", code: "id" }
	)

	if (isFetchingComponent([isFetchingOrganizations, isFetchingRoles]))
		return <Skeleton />

	return (
		<div className="form-users">
			<div className="row">
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.users.name" />
					</Heading>
					<Input
						id="firstName"
						value={formData.firstName}
						data={formData}
						setData={setFormData}
						disabled={disabled?.firstName}
						validators={[isEmptyString]}
					/>
				</div>
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.users.lastName" />
					</Heading>
					<Input
						id="lastName"
						value={formData.lastName}
						data={formData}
						setData={setFormData}
						disabled={disabled?.lastName}
						validators={[isEmptyString]}
					/>
				</div>
			</div>
			<div className="row">
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.users.organization" />
					</Heading>
					<Select
						id="organization"
						options={organizationMappedData}
						onChange={(value) =>
							setFormData({
								...formData,
								organization: { id: value[0].code, name: value[0].name },
							})
						}
						isClearable={false}
						hasTitle={false}
						initialValue={initialValue}
						selected={mapData([formData.organization], {
							name: "name",
							code: "id",
						})}
						isDisabled={disabled?.organization}
					/>
				</div>
				<div className="text-field">
					<Heading as="h5" UNSAFE_className="title">
						<FormattedMessage id="phronesis.usermanagement.users.email" />
					</Heading>
					<Input
						id="email"
						value={formData.email}
						data={formData}
						setData={setFormData}
						disabled={disabled?.email}
						validators={[isEmptyString, isEmailValidator]}
					/>
				</div>
			</div>

			<div className="checkbox-list">
				<CheckboxValidatorTitle
					items={formData.roles}
					title="phronesis.usermanagement.users.roles"
				/>
				{rolesData.roles?.map((item: Rol) => (
					<Checkbox
						key={item.name}
						label={item.name}
						className="checkbox"
						checked={formData.roles.includes(item.name)}
						onChange={() =>
							setFormData({
								...formData,
								roles: onChangeCheckbox(formData.roles, item.name),
							})
						}
						disabled={disabled?.roles}
					/>
				))}
			</div>
		</div>
	)
}

export default FormUsers
